import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2, RefreshCw, SkipForward, MailCheck, Clock, Loader,
  Smartphone, GripVertical, Save, RotateCcw, Plus, Upload, X,
} from 'lucide-react';
import { adminFetch, adminFetchJson } from '../../lib/adminAuth';
import { toast } from 'sonner';
import ArtworkPickerModal from './ArtworkPickerModal';

const MAX_TILES = 9;

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * WeeklyNewArrivalsPreview — approval gate for the automated Saturday
 * "New Arrivals" newsletter.
 *
 * Now supports:
 *   • Drag-to-reorder the 9 candidate works so the newsletter leads with
 *     the hero piece. Order is dirty until "Save order" is pressed.
 *   • "Send preview to my mobile" — one-off preview email to any address.
 */
const WeeklyNewArrivalsPreview = () => {
  const [state, setState] = useState({ loading: true, data: null, error: '' });
  const [busy, setBusy] = useState('');
  const [order, setOrder] = useState([]);      // local artwork order (drag state)
  const [subject, setSubject] = useState(''); // editable subject line
  const [previewTo, setPreviewTo] = useState('');
  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  // Ref to the iframe so we can re-render without a full reload on order-save.
  const iframeRef = useRef(null);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await adminFetchJson(`${API_URL}/api/admin/campaigns/new-arrivals/preview`);
      setState({ loading: false, data, error: '' });
      setOrder(data.artworks || []);
      setSubject(data.subject || 'New arrivals · Zebra One Gallery');
    } catch (e) {
      setState({ loading: false, data: null, error: e.message || 'Failed to load preview' });
    }
  };

  useEffect(() => { load(); }, []);

  const call = async (path, label, method = 'POST') => {
    setBusy(label);
    try {
      const res = await adminFetch(`${API_URL}${path}`, { method });
      if (!res.ok) throw new Error(await res.text());
      toast.success(`${label} — done`);
      await load();
    } catch (e) {
      toast.error(`${label} failed: ${e.message}`);
    } finally {
      setBusy('');
    }
  };

  // Drag handlers — HTML5 native so no extra dependency required for 9 tiles.
  const onDragStart = (i) => (e) => {
    setDragIndex(i);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox requires setData or the drag won't fire.
    e.dataTransfer.setData('text/plain', String(i));
  };
  const onDragOver = (i) => (e) => { e.preventDefault(); setHoverIndex(i); };
  const onDragLeave = () => setHoverIndex(null);
  const onDrop = (i) => (e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) { setDragIndex(null); setHoverIndex(null); return; }
    setOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setDragIndex(null); setHoverIndex(null);
  };
  const onDragEnd = () => { setDragIndex(null); setHoverIndex(null); };

  const originalOrderIds = useMemo(
    () => (state.data?.artworks || []).map((w) => w.id).join(','),
    [state.data?.artworks],
  );
  const currentOrderIds = useMemo(() => order.map((w) => w.id).join(','), [order]);
  const originalSubject = state.data?.subject || 'New arrivals · Zebra One Gallery';
  const orderDirty = originalOrderIds !== currentOrderIds;
  const subjectDirty = subject.trim() !== originalSubject.trim();
  const dirty = orderDirty || subjectDirty;

  const saveOrder = async () => {
    setBusy('SaveOrder');
    try {
      const pendingId = state.data?.pending?.id;
      const wasApproved = state.data?.pending?.status === 'approved';
      const res = await adminFetch(`${API_URL}/api/admin/campaigns/new-arrivals/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pending_id: pendingId, artworks: order, subject }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success(wasApproved ? 'Saved (still approved for Saturday)' : 'Saved & approved for Saturday');
      await load();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setBusy('');
    }
  };

  const resetOrder = () => {
    setOrder(state.data?.artworks || []);
    setSubject(originalSubject);
  };

  const removeTile = (idx) => {
    setOrder((prev) => prev.filter((_, i) => i !== idx));
  };

  const addFromLibrary = (artwork) => {
    setOrder((prev) => {
      if (prev.length >= MAX_TILES) {
        toast.error(`Newsletter is capped at ${MAX_TILES} works`);
        return prev;
      }
      if (prev.some((w) => w.id === artwork.id)) return prev;
      return [
        ...prev,
        {
          id: artwork.id,
          title: artwork.title,
          artistName: artwork.artistName,
          imageUrl: artwork.imageUrl,
          slug: artwork.slug,
        },
      ];
    });
  };

  const onUploadClick = () => {
    if (order.length >= MAX_TILES) {
      toast.error(`Newsletter is capped at ${MAX_TILES} works — remove one first`);
      return;
    }
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow same-file re-upload
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'newsletter');
      // Note: DO NOT set Content-Type — browser sets multipart boundary.
      const res = await adminFetch(`${API_URL}/api/admin/upload`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const url = data.url || data.path;
      if (!url) throw new Error('Upload response missing url');
      // Prompt for a caption so the tile isn't nameless in the email
      const caption = (window.prompt(
        'Caption (Artist — Title). Leave blank to omit.',
        file.name.replace(/\.[^.]+$/, ''),
      ) || '').trim();
      const [artistPart, titlePart] = caption.split(/\s*[—-]\s*/, 2);
      const linkTo = (window.prompt(
        'Link to (optional URL). Leave blank to link to /shop.',
        '',
      ) || '').trim();
      setOrder((prev) => [
        ...prev,
        {
          id: `custom-${Date.now()}`,
          title: (titlePart || caption || 'Untitled').slice(0, 120),
          artistName: (artistPart || '').slice(0, 80),
          imageUrl: url,
          href: linkTo || undefined,
        },
      ]);
      toast.success('Image added to newsletter (unsaved — click Save)');
    } catch (err) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const sendMobilePreview = async () => {
    if (!previewTo.trim() || !previewTo.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setBusy('SendMobile');
    try {
      const res = await adminFetch(`${API_URL}/api/admin/campaigns/new-arrivals/send-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: previewTo.trim(), artworks: order, subject }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      if (data.sent === false && data.reason === 'smtp_disabled_in_preview') {
        toast.info(`Preview OK (SMTP disabled in preview env) — will actually mail in production`);
      } else {
        toast.success(`Preview sent to ${previewTo}`);
      }
    } catch (e) {
      toast.error(`Send preview failed: ${e.message}`);
    } finally {
      setBusy('');
    }
  };

  if (state.loading) {
    return (
      <div className="p-6 border border-gray-200 rounded-lg flex items-center gap-2 text-gray-600" data-testid="newsletter-preview-loading">
        <Loader className="w-4 h-4 animate-spin" />
        Loading Saturday newsletter preview…
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="p-6 border border-red-200 bg-red-50 rounded-lg text-red-800">
        {state.error}
      </div>
    );
  }

  const { pending, subject: _serverSubject, subscriber_count, html } = state.data || {};
  const status = pending?.status || 'not_scheduled';
  const statusPill = (() => {
    if (status === 'approved') return { text: 'Approved — will send Saturday 10:00', cls: 'bg-green-100 text-green-800' };
    if (status === 'pending') return { text: 'Pending your approval', cls: 'bg-amber-100 text-amber-800' };
    if (status === 'skipped') return { text: 'Skipped for this week', cls: 'bg-gray-100 text-gray-700' };
    if (status === 'sent') return { text: 'Already sent this week', cls: 'bg-blue-100 text-blue-800' };
    return { text: 'No pending campaign — refresh to build one', cls: 'bg-gray-100 text-gray-700' };
  })();

  return (
    <div
      className="border border-gray-200 rounded-lg p-6 mb-6 bg-white"
      data-testid="newsletter-preview-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[3px] text-gray-500 mb-1">
            <MailCheck className="w-3.5 h-3.5" />
            Saturday newsletter · Preview + Approve
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-xs uppercase tracking-[3px] text-gray-400 flex-none">Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="New arrivals · Zebra One Gallery"
              className="flex-1 min-w-[280px] text-2xl font-light bg-transparent border-b border-transparent hover:border-gray-200 focus:border-black focus:outline-none py-1 px-0"
              maxLength={120}
              data-testid="newsletter-preview-subject"
            />
          </div>
          <div className="text-[11px] text-gray-400 mt-1">
            {subject.length}/120 · what recipients will see in their inbox
            {subjectDirty && <span className="ml-2 text-amber-600">· unsaved</span>}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded ${statusPill.cls}`} data-testid="newsletter-preview-status">
              <Clock className="w-3 h-3" />
              {statusPill.text}
            </span>
            <span className="text-gray-500">·</span>
            <span className="text-gray-600" data-testid="newsletter-preview-counts">
              {order.length} works · {subscriber_count?.toLocaleString()} subscribers
            </span>
            {dirty && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 border border-amber-300 text-[11px] text-amber-800" data-testid="newsletter-preview-dirty">
                Unsaved changes
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => call('/api/admin/campaigns/new-arrivals/refresh', 'Refresh')}
            disabled={!!busy}
            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
            data-testid="newsletter-preview-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${busy === 'Refresh' ? 'animate-spin' : ''}`} />
            Refresh selection
          </button>
          <button
            type="button"
            onClick={() => call('/api/admin/campaigns/new-arrivals/skip', 'Skip')}
            disabled={!!busy || status === 'skipped' || status === 'sent'}
            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
            data-testid="newsletter-preview-skip"
          >
            <SkipForward className="w-4 h-4" />
            Skip this week
          </button>
          <button
            type="button"
            onClick={async () => {
              setBusy('Approve');
              try {
                const res = await adminFetch(`${API_URL}/api/admin/campaigns/new-arrivals/approve`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ pending_id: pending?.id, artworks: order, subject }),
                });
                if (!res.ok) throw new Error(await res.text());
                toast.success('Approved — will send Saturday 10:00 London');
                await load();
              } catch (e) {
                toast.error(`Approve failed: ${e.message}`);
              } finally {
                setBusy('');
              }
            }}
            disabled={!!busy || status === 'sent' || !order.length}
            className="px-4 py-2 text-sm bg-black text-white rounded hover:bg-gray-800 flex items-center gap-2 disabled:opacity-50"
            data-testid="newsletter-preview-approve"
          >
            <CheckCircle2 className="w-4 h-4" />
            {status === 'approved' ? (dirty ? 'Save + Approve' : 'Approved') : 'Approve for Saturday'}
          </button>
        </div>
      </div>

      {/* Send preview to mobile — one-line row */}
      <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
        <Smartphone className="w-4 h-4 text-gray-500" />
        <span className="text-xs uppercase tracking-[3px] text-gray-500">Preview on phone</span>
        <input
          type="email"
          value={previewTo}
          onChange={(e) => setPreviewTo(e.target.value)}
          placeholder="your@email.com"
          className="flex-1 min-w-[240px] max-w-md px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-black"
          data-testid="newsletter-preview-mobile-input"
        />
        <button
          type="button"
          onClick={sendMobilePreview}
          disabled={!!busy || !previewTo}
          className="px-4 py-2 text-sm border border-gray-800 text-gray-800 rounded hover:bg-gray-800 hover:text-white flex items-center gap-2 disabled:opacity-50 transition-colors"
          data-testid="newsletter-preview-send-mobile"
        >
          {busy === 'SendMobile' ? <Loader className="w-4 h-4 animate-spin" /> : <MailCheck className="w-4 h-4" />}
          Send preview
        </button>
      </div>

      {/* Drag-to-reorder tiles */}
      <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs uppercase tracking-[3px] text-gray-500">
          Drag to reorder — first tile leads the email
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            disabled={!!busy || order.length >= MAX_TILES}
            className="px-3 py-1 text-xs border border-gray-800 text-gray-800 rounded hover:bg-gray-800 hover:text-white flex items-center gap-1 disabled:opacity-40 transition-colors"
            data-testid="newsletter-preview-add-from-library"
          >
            <Plus className="w-3 h-3" /> Add from library
          </button>
          <button
            type="button"
            onClick={onUploadClick}
            disabled={!!busy || uploading || order.length >= MAX_TILES}
            className="px-3 py-1 text-xs border border-gray-800 text-gray-800 rounded hover:bg-gray-800 hover:text-white flex items-center gap-1 disabled:opacity-40 transition-colors"
            data-testid="newsletter-preview-upload-device"
          >
            {uploading ? <Loader className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            Upload from device
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onFileSelected}
            className="hidden"
            data-testid="newsletter-preview-file-input"
          />
          {dirty && (
            <>
              <button
                type="button"
                onClick={resetOrder}
                disabled={!!busy}
                className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1 disabled:opacity-50"
                data-testid="newsletter-preview-reset-order"
              >
                <RotateCcw className="w-3 h-3" /> Reset
              </button>
              <button
                type="button"
                onClick={saveOrder}
                disabled={!!busy}
                className="px-3 py-1 text-xs bg-black text-white rounded hover:bg-gray-800 flex items-center gap-1 disabled:opacity-50"
                data-testid="newsletter-preview-save-order"
              >
                {busy === 'SaveOrder' ? <Loader className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Save changes
              </button>
            </>
          )}
        </div>
      </div>
      <div
        className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2 mb-4 select-none"
        data-testid="newsletter-preview-thumbs"
      >
        {order.map((w, i) => (
          <div
            key={w.id}
            draggable
            onDragStart={onDragStart(i)}
            onDragOver={onDragOver(i)}
            onDragLeave={onDragLeave}
            onDrop={onDrop(i)}
            onDragEnd={onDragEnd}
            className={`text-[11px] text-gray-600 cursor-move rounded transition-all ${
              hoverIndex === i && dragIndex !== null && dragIndex !== i
                ? 'ring-2 ring-black -translate-y-0.5'
                : ''
            } ${dragIndex === i ? 'opacity-40' : ''}`}
            data-testid={`newsletter-preview-tile-${i}`}
          >
            <div className="relative bg-gray-50 aspect-square flex items-center justify-center overflow-hidden border border-gray-100">
              {w.imageUrl ? (
                <img
                  src={w.imageUrl.startsWith('http') ? w.imageUrl : `${API_URL}${w.imageUrl}`}
                  alt={w.title}
                  className="w-full h-full object-contain pointer-events-none"
                  loading="lazy"
                />
              ) : null}
              <div className="absolute top-1 left-1 bg-black text-white text-[10px] leading-none px-1.5 py-0.5 rounded font-medium">
                {i + 1}
              </div>
              <div className="absolute top-1 right-1 bg-white/90 text-gray-600 rounded p-0.5">
                <GripVertical className="w-3 h-3" />
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeTile(i); }}
                onMouseDown={(e) => e.stopPropagation()}
                onDragStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
                title="Remove from newsletter"
                className="absolute bottom-1 right-1 bg-white/90 hover:bg-red-600 hover:text-white text-gray-700 rounded p-0.5 transition-colors shadow-sm"
                data-testid={`newsletter-preview-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <div className="mt-1 truncate" title={`${w.artistName} — ${w.title}`}>
              {w.artistName}
            </div>
            <div className="text-gray-400 truncate">{w.title}</div>
          </div>
        ))}
      </div>

      {/* Full email preview — rendered in an isolated iframe so the CSS
          can't bleed into the admin dashboard chrome. Preview reflects the
          SAVED order; unsaved reorders show up here after "Save order". */}
      <details className="mt-4 border border-gray-200 rounded" open>
        <summary className="px-4 py-2 text-xs uppercase tracking-[3px] text-gray-500 cursor-pointer select-none">
          Full email preview {dirty ? '(server copy — save order to refresh)' : ''}
        </summary>
        <iframe
          title="newsletter-preview"
          srcDoc={html || ''}
          className="w-full h-[520px] bg-white"
          data-testid="newsletter-preview-iframe"
          ref={iframeRef}
        />
      </details>

      <ArtworkPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={addFromLibrary}
        existingIds={order.map((w) => w.id)}
        maxSelect={MAX_TILES - order.length}
      />
    </div>
  );
};

export default WeeklyNewArrivalsPreview;
